import { useState, useRef, useEffect, useCallback, useId } from 'react'
import { ChevronDownIcon } from './queue/queueIcons'
import usePreferencesStore from '@store/preferencesStore'
import reportViewsService from '@api/v1/reportViewsService'
import { SHARED_VIEWS } from '@config/featureFlags'
import { trackSavedView } from '@/analytics/pendo'
import { reportRefFromStorageKey } from '@/analytics/gridTransforms'
import { signedInAsSysAdmin, othersReadFailure } from '@utils/sysAdmin'

/**
 * Named report views (CC-49336, CC-49595, CC-50282 Phase 2): save / apply /
 * rename / delete / re-share a named combination of filter selections + column
 * layout (visibility, widths, order) + sort + page state for one report.
 *
 * `currentFilters` = the ReportView filter values to snapshot on save.
 * `getPageState()` = optional getter for the rest of the page's working state
 *                    (CC-49732): { groupBy, grid: { columnFilters, toggles } }.
 *                    Views saved with it are stamped v:2; v1 views (filters +
 *                    layout only) still apply exactly as before.
 * `onApply(view)`  = ReportView restores filters (+ page state for v2) and
 *                    remounts the grid so it re-reads the layout this menu
 *                    wrote back.
 *
 * ## "Only me" is a scope now, not a different storage system (CC-50282)
 *
 * Personal views used to live in `preferencesStore` — i.e. in this browser. That
 * made three things true that nobody wanted: a view did not follow its author to
 * a second machine, there was no way to promote one to a group short of
 * rebuilding the entire page state under a new name, and clearing site data
 * destroyed it. Meanwhile group/firm views lived in D1. One product concept, two
 * stores, and a menu that had to explain itself ("My views (this device)").
 *
 * Now all three scopes are rows in one table with one vocabulary
 * (`cb-api/src/domains/reports/scope.js`), so the promote/demote control is a
 * `<select>` per row rather than a feature request.
 *
 * **Device-local views are adopted, never discarded.** The first time this menu
 * opens it hands the WHOLE localStorage store to the account at "Only me" scope —
 * every report's views, not just this one's, because a view for a report the user
 * never opens again would otherwise sit invisible forever, and a migration that
 * quietly leaves things behind is worse than none. The import is idempotent
 * server-side (an existing name is skipped, never overwritten, so a second
 * machine cannot revert the first's edits), so a retry costs nothing.
 *
 * The localStorage copy is deliberately LEFT ALONE afterwards: deleting the only
 * other copy of a user's work to tidy up is not a trade worth making. Anything
 * that failed to adopt keeps showing under "Still on this device only", and
 * reopening the menu tries again.
 *
 * With `SHARED_VIEWS` off — which is the case in PROD today — none of the above
 * runs and the menu behaves exactly as it did before: local views, local saves.
 */

const SCOPE_OPTIONS = [
  { value: 'mine', label: 'Only me' },
  { value: 'group', label: 'A group…' },
  { value: 'firm', label: 'Everyone at the firm' },
]

/** How a row states its own audience. */
const scopeSummary = (v) => {
  if (v.scope === 'firm') return 'Everyone at the firm'
  if (v.scope === 'group') return v.groupName ? `Group: ${v.groupName}` : 'A group'
  return 'Only me'
}

/**
 * Spec 173 — how a row in "Other users’ views" states WHOSE it is. Never "Only me":
 * these rows belong to someone else, and the words must say so before anyone applies one.
 */
const otherSummary = (v) => {
  const who = v.creatorName || 'another user'
  if (v.scope === 'mine') return `Private to ${who}`
  if (v.scope === 'group') return `${v.groupName ? `${v.groupName} group` : 'A group you’re not in'} · ${who}`
  return `${scopeSummary(v)} · ${who}`
}

const SectionLabel = ({ children, divide }) => (
  <p
    className={`px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 ${
      divide ? 'mt-1 border-t border-gray-100 pt-1.5' : ''
    }`}
  >
    {children}
  </p>
)

const ReportViewsMenu = ({ storageKey, currentFilters, onApply, getPageState }) => {
  const prefs = usePreferencesStore()
  /*
   * spec 094 — Saved View analytics.
   *
   * 🔴 The view NAME never leaves the browser. Users name saved views after
   * clients, matters and partners ("Smith trust — Q3", "Bergman's book"), so a
   * view name is firm data in the same way a filter value is. Only the action
   * and the low-cardinality scope token go out. Same reason the group NAME is
   * not sent for a group-scoped view.
   */
  const trackView = useCallback((action, scope) => {
    const reportId = reportRefFromStorageKey(storageKey)
    if (reportId == null) return
    trackSavedView({ action, reportId, scope: scope || 'mine' })
  }, [storageKey])
  const [open, setOpen] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [renaming, setRenaming] = useState(null) // { id, local, from, to }
  const [savedFlash, setSavedFlash] = useState(null)
  const [scope, setScope] = useState('mine') // scope for the NEXT save
  const [shareGroupId, setShareGroupId] = useState('')
  const [groups, setGroups] = useState(null) // null = not loaded yet
  const [server, setServer] = useState({ views: [], storageConfigured: true, scopesResolved: true })
  const [rescoping, setRescoping] = useState(null) // { id, scope, groupId } — a row mid-move
  const [error, setError] = useState(null)
  const [note, setNote] = useState(null) // import / re-scope confirmations
  const [busy, setBusy] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!savedFlash) return undefined
    const t = setTimeout(() => setSavedFlash(null), 2000)
    return () => clearTimeout(t)
  }, [savedFlash])

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  /** Every device-local view, across every report, as import entries. */
  const localEntries = useCallback(() => {
    const all = usePreferencesStore.getState().namedViews || {}
    const out = []
    for (const [reportKey, byName] of Object.entries(all)) {
      for (const [name, view] of Object.entries(byName || {})) out.push({ reportKey, name, view })
    }
    return out
  }, [])

  /**
   * Load the server-side views, handing this device's store over on the way
   * through (once per mount — the import is idempotent, so a repeat is a no-op
   * that costs one request).
   *
   * A list failure stays quiet: the local views must keep working when the store
   * is unreachable. An ADOPTION failure does not — a user who believes their views
   * moved and finds they didn't is the whole problem being fixed here.
   */
  const handedOver = useRef(false)
  const refresh = useCallback(async () => {
    if (!SHARED_VIEWS || !storageKey) return
    try {
      let env = await reportViewsService.list(storageKey)
      if (env.storageConfigured && !handedOver.current) {
        const entries = localEntries()
        if (entries.length === 0) {
          handedOver.current = true
        } else {
          try {
            const res = await reportViewsService.importLocal(entries)
            handedOver.current = true
            if (res.imported > 0) {
              setNote(
                `Moved ${res.imported} saved view${res.imported === 1 ? '' : 's'} from this device into your account.`
              )
              env = await reportViewsService.list(storageKey)
            }
          } catch {
            // Left false on purpose: reopening the menu retries.
            setError('Could not move this device’s saved views into your account — they are still listed below.')
          }
        }
      }
      setServer(env)
    } catch {
      setServer({ views: [], storageConfigured: true, scopesResolved: true })
    }
  }, [storageKey, localEntries])

  useEffect(() => {
    if (open) refresh()
  }, [open, refresh])

  // Groups load lazily — only once something actually needs the picker.
  const needGroups = scope === 'group' || rescoping?.scope === 'group'
  useEffect(() => {
    if (!SHARED_VIEWS || !needGroups || groups !== null) return undefined
    let stale = false
    reportViewsService
      .groups()
      .then((g) => { if (!stale) setGroups(g) })
      .catch(() => { if (!stale) setGroups([]) })
    return () => { stale = true }
  }, [needGroups, groups])

  /*
   * Spec 173 — "Other users’ views", for CB's SysAdmin account only, read-only.
   *
   * Kept apart from everything above on purpose. These rows are NEVER put into
   * `server.views`, so they never reach `serverMine` / `adopted` / `totalCount`: merged,
   * another user's private "Q3 cut" would be listed under "Only me" AND would hide the
   * admin's own on-device "Q3 cut" (the `adopted` filter drops a local view whose name
   * is already in the account). They have their own state and their own row renderer,
   * which has no rename, share or delete control to offer.
   *
   * Lazy twice over: whether the signed-in token is the SysAdmin is checked only when the
   * menu opens (a display hint — cb-api is the authority), and the views themselves are
   * read only when the SysAdmin expands the section, because each read costs cb-api a CB
   * admin check. Every opening of the menu starts collapsed.
   */
  const [sysAdmin, setSysAdmin] = useState(false)
  const [othersOpen, setOthersOpen] = useState(false)
  const [others, setOthers] = useState({ status: 'idle', views: [], error: null, retryable: true })
  const othersReq = useRef(0)
  const othersToggleId = useId()
  const othersRegionId = useId()

  useEffect(() => {
    if (!open || !SHARED_VIEWS || !storageKey) return undefined
    let stale = false
    signedInAsSysAdmin().then((yes) => { if (!stale) setSysAdmin(yes) })
    return () => {
      stale = true
      othersReq.current += 1 // an answer still in flight belongs to the closed menu
      setOthersOpen(false)
    }
  }, [open, storageKey])

  const loadOthers = useCallback(async () => {
    const req = ++othersReq.current
    setOthers({ status: 'loading', views: [], error: null, retryable: true })
    try {
      const env = await reportViewsService.list(storageKey, { includeOthers: true })
      if (req !== othersReq.current) return
      const views = [...env.others].sort(
        (a, b) => (a.creatorName || '').localeCompare(b.creatorName || '') || a.name.localeCompare(b.name)
      )
      setOthers({ status: 'ready', views, error: null, retryable: true })
    } catch (err) {
      if (req !== othersReq.current) return
      const { message, retryable } = othersReadFailure(err)
      setOthers({ status: 'failed', views: [], error: message, retryable })
    }
  }, [storageKey])

  const toggleOthers = () => {
    const next = !othersOpen
    setOthersOpen(next)
    if (next) loadOthers()
  }

  if (!storageKey) return null

  const localAll = prefs.getNamedViews?.(storageKey) || {}
  const serverMine = server.views.filter((v) => v.scope === 'mine')
  const serverGroup = server.views.filter((v) => v.scope === 'group')
  const serverFirm = server.views.filter((v) => v.scope === 'firm')
  // A local view whose name is already in the account IS that view, adopted —
  // listing it twice would make one view look like two.
  const adopted = new Set(SHARED_VIEWS && server.storageConfigured ? serverMine.map((v) => v.name) : [])
  const localNames = Object.keys(localAll)
    .filter((n) => !adopted.has(n))
    .sort((a, b) => a.localeCompare(b))
  const totalCount = server.views.length + localNames.length
  const groupNameFor = (id) => (groups || []).find((g) => String(g.id) === String(id))?.name || null

  // The full view payload a saved view carries. CC-49732: v2 captures the whole
  // page state, not just filters + layout.
  const buildViewPayload = () => ({
    v: 2,
    filters: currentFilters || {},
    columnVisibility: prefs.getColumnVisibility?.(storageKey) || {},
    columnWidths: prefs.getColumnWidths?.(storageKey) || {},
    columnOrder: prefs.getColumnOrder?.(storageKey) || null,
    sorting: prefs.getSorting?.(storageKey) || null,
    columnAlignment: prefs.getColumnAlignment?.(storageKey) || null,
    pageState: getPageState?.() || null,
    savedAt: new Date().toISOString(),
  })

  const saveCurrent = async () => {
    const name = nameInput.trim()
    if (!name) return
    setError(null)
    setNote(null)
    const payload = buildViewPayload()
    // Without the flag there is no server to save to — keep the original
    // behaviour rather than failing.
    if (!SHARED_VIEWS || !server.storageConfigured) {
      // spec 094 — the view NAME is never sent: users name views after clients
      // and matters. Only the action and the scope token.
      trackView('save', 'mine')
      prefs.saveNamedView?.(storageKey, name, payload)
      setNameInput('')
      setSavedFlash(name) // bug #1: saving silently looked like it did nothing
      return
    }
    if (scope === 'group' && !shareGroupId) {
      setError('Pick a group to share with.')
      return
    }
    setBusy(true)
    try {
      await reportViewsService.save(storageKey, {
        name,
        scope,
        groupId: scope === 'group' ? shareGroupId : null,
        groupName: scope === 'group' ? groupNameFor(shareGroupId) : null,
        view: payload,
      })
      trackView('save', scope)
      await refresh()
      setNameInput('')
      setSavedFlash(name)
    } catch (err) {
      setError(err?.response?.data?.error || 'Saving is unavailable right now — the view was not saved.')
    } finally {
      setBusy(false)
    }
  }

  // Shared/server apply path — write the layout back so the (remounted) grid
  // picks it up, exactly like a local view.
  const applyView = (view) => {
    if (!view) return
    prefs.setColumnVisibility?.(storageKey, view.columnVisibility || {})
    prefs.setColumnWidths?.(storageKey, view.columnWidths || {})
    if (view.columnOrder) prefs.setColumnOrder?.(storageKey, view.columnOrder)
    if (view.sorting) prefs.setSorting?.(storageKey, view.sorting)
    // v2 (CC-49732/CC-49735): alignment overrides ride the view too. Restore
    // exactly the saved set — drop current overrides the view doesn't carry.
    if (view.v >= 2) {
      const saved = view.columnAlignment || {}
      const current = prefs.getColumnAlignment?.(storageKey) || {}
      for (const colId of Object.keys(current)) {
        if (!saved[colId]) prefs.setColumnAlignment?.(storageKey, colId, null)
      }
      for (const [colId, align] of Object.entries(saved)) {
        prefs.setColumnAlignment?.(storageKey, colId, align)
      }
    }
    trackView('apply', view?.scope)
    setOpen(false)
    onApply?.(view)
  }

  const removeServerView = async (v) => {
    setError(null)
    try {
      await reportViewsService.remove(storageKey, v.id)
      trackView('delete', v?.scope)
      await refresh()
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not delete that view.')
    }
  }

  /** Promote / demote in place — same id, same payload, new audience. */
  const commitScope = async (v, nextScope, groupId) => {
    setError(null)
    setNote(null)
    setBusy(true)
    try {
      await reportViewsService.update(storageKey, v.id, {
        scope: nextScope,
        groupId: nextScope === 'group' ? groupId : null,
        groupName: nextScope === 'group' ? groupNameFor(groupId) : null,
      })
      setRescoping(null)
      await refresh()
      setNote(`“${v.name}” is now visible to: ${SCOPE_OPTIONS.find((o) => o.value === nextScope)?.label.replace('…', '') || nextScope}.`)
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not change who can see that view.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * The row's scope `<select>` changed.
   *
   * "A group…" is a TWO-step choice — pick the group, then Share — so it only opens
   * the picker. Re-picking it on a row already at group scope means "change which
   * group", which is why there is no early return for it.
   *
   * **Order matters here, and getting it wrong is CC-50282's QA failure.** The old
   * guard was `if (next === v.scope && next !== 'group') return`, i.e. it compared
   * the new choice against the row's PERSISTED scope and bailed before clearing
   * `rescoping`. So: open the group picker on an "Only me" row, change your mind,
   * pick "Only me" again → the guard returned early, `rescoping` stayed set, and
   * because the select is driven by `rescoping.scope` it SNAPPED BACK to "A group…"
   * with the picker still open. Every further attempt hit the same early return, so
   * the row was stuck: the dropdown appeared to change, nothing persisted, and no
   * message explained it. That is exactly what QA reported on 2026-08-14.
   *
   * So abandon any pending pick FIRST, and only then decide whether there is
   * anything to write.
   */
  const onScopeSelect = (v, next) => {
    if (next === 'group') {
      setRescoping({ id: v.id, scope: 'group', groupId: v.groupId || '' })
      return
    }
    const wasPending = rescoping?.id === v.id
    setRescoping(null)
    // Already at this scope: abandoning the pending pick WAS the whole action, and
    // closing the picker is its own feedback. Nothing to persist.
    if (next === v.scope) {
      if (wasPending) setNote(null)
      return
    }
    commitScope(v, next, null)
  }

  const commitRename = async () => {
    const r = renaming
    setRenaming(null)
    if (!r) return
    const to = r.to.trim()
    if (!to || to === r.from) return
    if (r.local) {
      trackView('rename', 'mine')
      prefs.renameNamedView?.(storageKey, r.from, to)
      return
    }
    setError(null)
    try {
      await reportViewsService.update(storageKey, r.id, { name: to })
      trackView('rename', r?.scope)
      await refresh()
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not rename that view.')
    }
  }

  const renameInput = (r) => (
    <input
      autoFocus
      value={r.to}
      aria-label={`New name for ${r.from}`}
      onChange={(e) => setRenaming({ ...r, to: e.target.value })}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commitRename()
        if (e.key === 'Escape') setRenaming(null)
      }}
      onBlur={commitRename}
      className="flex-1 rounded-rc-control border border-rc-control px-2 py-1 text-sm"
    />
  )

  /** A server-side view. Owners get the scope control; everyone gets Apply. */
  const serverRow = (v) => (
    <div key={v.id}>
      <div className="group flex items-center gap-1 px-1 py-0.5">
        {renaming?.id === v.id ? (
          renameInput(renaming)
        ) : (
          <button
            type="button"
            onClick={() => applyView(v.view)}
            title={v.isOwner ? 'Apply this view' : `Apply this view (shared by ${v.creatorName || 'a teammate'})`}
            className="min-w-0 flex-1 py-1 text-left text-sm text-gray-700 hover:text-cb-accent-text"
          >
            <span className="block truncate">{v.name}</span>
            <span className="block truncate text-[10px] text-gray-400">
              {scopeSummary(v)}
              {v.isOwner ? '' : ` · ${v.creatorName || 'unknown'}`}
            </span>
          </button>
        )}
        {v.isOwner && (
          <select
            aria-label={`Who can see ${v.name}`}
            title={`Change who can see “${v.name}”`}
            value={rescoping?.id === v.id ? rescoping.scope : v.scope}
            onChange={(e) => onScopeSelect(v, e.target.value)}
            disabled={busy}
            className="max-w-[7.5rem] rounded-rc-control border border-rc-control px-1 py-0.5 text-[11px] text-gray-600 focus:border-rc-chip-line focus:ring-2 focus:ring-rc-chip-line/20"
          >
            {SCOPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
        {v.isOwner && (
          <button
            type="button"
            onClick={() => setRenaming({ id: v.id, local: false, from: v.name, to: v.name })}
            aria-label={`Rename view ${v.name}`}
            title="Rename"
            className="px-1 text-xs text-gray-500 hover:text-gray-800"
          >
            ✎
          </button>
        )}
        {v.canManage && (
          <button
            type="button"
            onClick={() => removeServerView(v)}
            aria-label={`Delete view ${v.name}`}
            title={v.isOwner ? 'Delete' : "Delete (you're an admin)"}
            className="px-1 text-xs text-gray-500 hover:text-red-700"
          >
            ×
          </button>
        )}
      </div>
      {rescoping?.id === v.id && rescoping.scope === 'group' && (
        <div className="mb-1 flex items-center gap-1.5 rounded bg-gray-50 px-1.5 py-1.5">
          <select
            aria-label={`Group to share ${v.name} with`}
            value={rescoping.groupId}
            onChange={(e) => setRescoping({ ...rescoping, groupId: e.target.value })}
            className="min-w-0 flex-1 rounded-rc-control border border-rc-control px-1.5 py-1 text-xs text-gray-600"
          >
            <option value="">{groups === null ? 'Loading groups…' : groups.length === 0 ? 'No groups found' : 'Pick a group…'}</option>
            {(groups || []).map((g) => (
              <option key={g.id} value={String(g.id)}>{g.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!rescoping.groupId || busy}
            onClick={() => commitScope(v, 'group', rescoping.groupId)}
            className="rounded-rc-control bg-cb-accent-text px-2 py-1 text-[11px] font-medium text-white hover:bg-cb-navy disabled:opacity-50"
          >
            Share
          </button>
          <button
            type="button"
            onClick={() => setRescoping(null)}
            className="px-1 text-[11px] text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )

  /**
   * Spec 173 — a row in "Other users’ views". Apply is the ONLY action: there is no
   * scope select, no rename and no delete here, whatever the server says about the row,
   * because the renderer has none to offer. cb-api refuses those writes regardless.
   */
  const otherRow = (v) => (
    <div key={v.id} className="flex items-center gap-1 px-1 py-0.5" data-testid="report-views-other-row">
      <button
        type="button"
        onClick={() => applyView(v.view)}
        title={`Apply this view (saved by ${v.creatorName || 'another user'})`}
        className="min-w-0 flex-1 py-1 text-left text-sm text-gray-700 hover:text-cb-accent-text"
      >
        <span className="block truncate">{v.name}</span>
        <span className="block truncate text-[11px] text-gray-600">{otherSummary(v)}</span>
      </button>
    </div>
  )

  return (
    <div ref={ref} className="relative">
      {/* An EXPLICIT aria-label, because every host wraps this trigger in
          `<label>Saved views …</label>` — and a wrapping label supplies the
          accessible name from its ENTIRE text content, which includes the open
          dropdown. Without this, focusing the button announced the whole panel
          ("Saved views Still on this device only Only me…") as its name, and it
          changed every time the panel's contents did. `aria-label` wins over a
          wrapping label, so the name is now stable and says what it does. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Saved views${totalCount > 0 ? ` (${totalCount})` : ''}`}
        data-pendo="report-views-menu"
        /* B3 (2026-08-26): .btn-rc, was .btn-cb-toolbar. This one line was the
           single biggest geometry disagreement left in the report chrome — the
           toolbar class is 30px tall / 8px radius / 12px text, and this control
           sits in Bar 2 between 38px filter pills and the 38px Run control on
           ALL 140 reports, so it read as a smaller, older thing wedged among
           them. .btn-rc is 38px / 6px / 14px and is what its neighbours already
           wear.

           This changes THIS control only. .btn-cb-toolbar itself is deliberately
           NOT resized: ~30 call sites across 16 files wear it, including nine
           non-Report-Center list pages reached through DataGrid's !reportChrome
           branch, and none of those bars have been through the refresh.

           `is-active` carries over unchanged — .btn-rc.is-active and
           .btn-cb-toolbar.is-active are the same chip treatment. */
        className={`btn-rc ${open ? 'is-active' : ''}`}
      >
        {/* B6 (spec 117) — outline chevron, was a filled ▾ sitting in the
            trigger's own text node.

            Safe to change, verified rather than assumed: this button's
            accessible name is the EXPLICIT aria-label three lines up, not its
            text content, so nothing that resolves this control by name can move.
            The two e2e specs that reach it both anchor on /saved views/i
            (report-views-scope.spec.js:53, reports-grid-audit.spec.js). That
            matters here more than anywhere else in this sweep: ReportViewsMenu is
            ONE component and all 21 specialized reports plus the generic path
            mount it, so a mistake would be 140 reports wide.

            `.btn-rc` already sets `gap: 0.5rem`, which is why the literal space
            before the old glyph is gone rather than replaced — keeping it would
            have made this the one control on the bar with 4px + 8px of caret
            clearance. */}
        Views{totalCount > 0 ? ` (${totalCount})` : ''}
        <ChevronDownIcon className="h-3.5 w-3.5 flex-none" />
      </button>
      {open && (
        <div
          /* right-0. With neither `left` nor `right` set, an absolutely
             positioned panel starts at its static position and grows RIGHTWARD,
             and all 21 hosts put this trigger inside ReportFilterPanel's
             right-anchored (ml-auto) action cluster — so the trigger's right edge
             sits a constant ~254px from the viewport edge and 384px of panel ran
             off it on every report at every width. Measured: 36–56px of overflow,
             which clipped ~43px of the 48px Save button. Not cosmetic.
             Anchoring right opens it leftward from the trigger instead.

             `max-w` caps the panel's WIDTH so it can never exceed the viewport;
             it does NOT guarantee the left edge stays on screen, because the
             right edge is pinned to the trigger rather than to the viewport. That
             holds while the content column is wider than ~638px, which it always
             is in this desktop shell (flex h-screen + fixed sidebar). A real
             clamp needs the portal+fixed treatment PopoverMenu already does —
             worth doing, but it is a refactor, not this fix.

             STILL TRUE, and now worth saying plainly: this is a SECOND popover
             implementation. common/PopoverMenu.jsx does portal + placement +
             flip + roving focus + focus return + click-outside, and this panel
             does none of it. That is a duplicate-primitive risk of exactly the
             kind ObjectLookup/LookupFilter already cost us — a fix landed on one
             will silently miss the other. Deliberately NOT migrated in this
             batch (it would change focus order and this control's e2e locators);
             the surface treatment below is aligned to PopoverMenu's so the two
             at least LOOK like one thing while they remain two.

             2026-08-26: rounded-rc-card + border-rc-line, was rounded-lg +
             border-gray-200. Same hexes (8px, #e5e7eb) — no visual change, and
             deliberately rc-LINE not rc-control: a floating card's outline is
             decorative and owes SC 1.4.11 nothing. The controls INSIDE it are
             the ones that moved to --color-rc-control. */
          className="absolute right-0 z-30 mt-1 w-96 max-w-[calc(100vw-2rem)] rounded-rc-card border border-rc-line bg-white p-2 shadow-lg"
        >
          {/* Fail-closed, and say so. A short list that looks complete is worse
              than an admitted gap — that ambiguity hid the CC-49595 group bug
              through two QA rounds. */}
          {SHARED_VIEWS && !server.scopesResolved && (
            <p className="mb-1 rounded bg-amber-50 px-1.5 py-1 text-[11px] font-medium text-amber-800" role="status">
              Couldn’t check your group membership, so group views may be missing from this list.
            </p>
          )}
          {SHARED_VIEWS && !server.storageConfigured && (
            <p className="mb-1 rounded bg-amber-50 px-1.5 py-1 text-[11px] font-medium text-amber-800" role="status">
              Saved-view storage isn’t configured in this environment — views stay on this device.
            </p>
          )}

          {SHARED_VIEWS && serverMine.length > 0 && (
            <>
              <SectionLabel>Only me</SectionLabel>
              {serverMine.map(serverRow)}
            </>
          )}
          {SHARED_VIEWS && serverGroup.length > 0 && (
            <>
              <SectionLabel divide={serverMine.length > 0}>Shared with your group</SectionLabel>
              {serverGroup.map(serverRow)}
            </>
          )}
          {SHARED_VIEWS && serverFirm.length > 0 && (
            <>
              <SectionLabel divide={serverMine.length + serverGroup.length > 0}>Shared with the firm</SectionLabel>
              {serverFirm.map(serverRow)}
            </>
          )}

          {(localNames.length > 0 || totalCount === 0) && (
            <SectionLabel divide={server.views.length > 0}>
              {SHARED_VIEWS ? 'Still on this device only' : 'Saved views'}
            </SectionLabel>
          )}
          {totalCount === 0 && (
            <p className="px-1 pb-2 text-xs text-gray-500">
              No saved views yet. Set up filters, columns, and sort, then save them as a named view.
            </p>
          )}
          {localNames.map((name) => (
            <div key={name} className="group flex items-center gap-1 px-1 py-0.5">
              {renaming?.local && renaming.from === name ? (
                renameInput(renaming)
              ) : (
                <button
                  type="button"
                  onClick={() => applyView(localAll[name])}
                  title="Apply this view"
                  className="flex-1 truncate py-1 text-left text-sm text-gray-700 hover:text-cb-accent-text"
                >
                  {name}
                </button>
              )}
              <button
                type="button"
                onClick={() => setRenaming({ id: null, local: true, from: name, to: name })}
                aria-label={`Rename view ${name}`}
                title="Rename"
                className="px-1 text-xs text-gray-500 hover:text-gray-800"
              >
                ✎
              </button>
              <button
                type="button"
                data-pendo="report-view-delete"
                onClick={() => { trackView('delete', 'mine'); prefs.deleteNamedView?.(storageKey, name) }}
                aria-label={`Delete view ${name}`}
                title="Delete"
                className="px-1 text-xs text-gray-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          ))}

          {/* Spec 173 — "Other users’ views": CB's SysAdmin account only, read-only, and
              separate from every list above (see the state block for why it must never be
              merged into them). A disclosure — a native button with aria-expanded — so it
              is collapsed until asked for and fully keyboard-operable. Its four states are
              never merged: loading, failed (with the server's own sentence), genuinely
              empty, and the rows. */}
          {SHARED_VIEWS && sysAdmin && server.storageConfigured && (
            <div className="mt-1 border-t border-gray-100 pt-1.5" data-testid="report-views-others">
              <h3 className="m-0">
                <button
                  type="button"
                  id={othersToggleId}
                  aria-expanded={othersOpen}
                  aria-controls={othersRegionId}
                  onClick={toggleOthers}
                  className="flex w-full items-center justify-between rounded px-1 py-1 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-600 hover:text-gray-900"
                >
                  Other users’ views
                  <ChevronDownIcon className={`h-3.5 w-3.5 flex-none ${othersOpen ? 'rotate-180' : ''}`} />
                </button>
              </h3>
              <div id={othersRegionId} role="region" aria-labelledby={othersToggleId} hidden={!othersOpen}>
                <p className="px-1 pb-1 text-[11px] text-gray-600">
                  Saved by other people at this firm. You can apply one, but not rename, share or delete it.
                </p>
                {others.status === 'loading' && (
                  <p className="px-1 pb-1 text-[11px] text-gray-600" role="status">
                    Loading other users’ views…
                  </p>
                )}
                {others.status === 'failed' && (
                  <div className="px-1 pb-1" role="alert">
                    <p className="text-[11px] font-medium text-red-700">Other users’ views were not loaded.</p>
                    {others.error && <p className="text-[11px] text-red-700">{others.error}</p>}
                    {others.retryable && (
                      <button
                        type="button"
                        onClick={loadOthers}
                        /* No hover fill: --color-rc-control is 3.03:1 on white but 2.896:1 on
                           #f9fafb (src/index.css), so a gray-50 hover would sink the border
                           under SC 1.4.11's 3.0 against its own fill. The text darkens instead. */
                        className="mt-1 rounded-rc-control border border-rc-control px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:text-gray-900"
                      >
                        Load them again
                      </button>
                    )}
                  </div>
                )}
                {others.status === 'ready' && others.views.length === 0 && (
                  <p className="px-1 pb-1 text-[11px] text-gray-600">
                    Nobody else has a saved view of this report that isn’t already in your list.
                  </p>
                )}
                {others.status === 'ready' && others.views.map(otherRow)}
              </div>
            </div>
          )}
          <div className="mt-1 flex items-center gap-1.5 border-t border-gray-100 px-1 pt-2">
            <input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveCurrent()
              }}
              placeholder="Name this view…"
              aria-label="View name"
              className="flex-1 rounded-rc-control border border-rc-control px-2 py-1 text-sm focus:border-rc-chip-line focus:ring-2 focus:ring-rc-chip-line/20"
            />
            <button
              type="button"
              data-pendo="report-view-save"
              onClick={saveCurrent}
              disabled={!nameInput.trim() || busy}
              className="rounded-rc-control bg-cb-accent-text px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-cb-navy disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
          {/* The audience for the view about to be SAVED — not for any row above.
              Those are two different jobs done by two `<select>`s carrying the same
              three option labels, and this one is the bigger, more prominent of the
              two while reading "Who can see this view", so it is the one a tester
              reaches for when they mean "share the view I already have". Changing it
              then does nothing visible and says nothing — which is one of the two
              ways CC-50282's promote looked broken. A visible caption tying it to the
              Save button above costs nothing and removes the ambiguity.

              The `aria-label` is deliberately UNCHANGED: three e2e specs and the
              unit suite address this control by it, including
              e2e/reports-grid-audit.spec.js, which another batch owns. An adjacent
              caption does not alter an aria-labelled control's accessible name. */}
          {SHARED_VIEWS && (
            <div className="flex items-center gap-1.5 px-1 pt-1.5">
              <span className="text-[11px] text-gray-500">New view can be seen by</span>
              <select
                aria-label="Who can see this view"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="rounded-rc-control border border-rc-control px-1.5 py-1 text-xs text-gray-600 focus:border-rc-chip-line focus:ring-2 focus:ring-rc-chip-line/20"
              >
                {SCOPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {scope === 'group' && (
                <select
                  aria-label="Group to share with"
                  value={shareGroupId}
                  onChange={(e) => setShareGroupId(e.target.value)}
                  className="min-w-0 flex-1 rounded-rc-control border border-rc-control px-1.5 py-1 text-xs text-gray-600 focus:border-rc-chip-line focus:ring-2 focus:ring-rc-chip-line/20"
                >
                  <option value="">{groups === null ? 'Loading groups…' : groups.length === 0 ? 'No groups found' : 'Pick a group…'}</option>
                  {(groups || []).map((g) => (
                    <option key={g.id} value={String(g.id)}>{g.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {error && (
            <p className="px-1 pt-1 text-[11px] font-medium text-red-700" role="alert">
              {error}
            </p>
          )}
          {note && (
            <p className="px-1 pt-1 text-[11px] font-medium text-cb-navy" role="status">
              {note}
            </p>
          )}
          {savedFlash && (
            <p className="px-1 pt-1 text-[11px] font-medium text-green-700" role="status">
              Saved “{savedFlash}” ✓
            </p>
          )}
          <p className="px-1 pt-1 text-[10px] text-gray-400">
            Captures the whole working state — run filters, columns, widths, order, sort, alignment, column filters, page toggles, and grouping.
            {SHARED_VIEWS
              ? ' Views are saved to your account, so they follow you between devices. Change who can see one at any time.'
              : ' Saved on this device, just for you.'}
          </p>
        </div>
      )}
    </div>
  )
}

export default ReportViewsMenu
